/**
 * Global external link guard hook
 * Intercepts external link clicks and prompts user to copy link
 * Solves WeChat mini-program WebView whitelist limitation
 */

import { useEffect } from 'react';
import {
  isExternalLink,
  isResourceFile,
} from '@/helper/util/external-link-guard';
import { copyToClipboard } from '@/helper/util/clipboard';
import { globalAlertStore } from '@/store/common';

export const useExternalLinkGuard = () => {
  useEffect(() => {
    /**
     * Handle link click event
     */
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Find closest <a> tag
      const link = target.closest('a');

      if (!link) {
        return;
      }

      const href = link.href;

      // Filter resource files
      if (isResourceFile(href)) {
        return;
      }

      // Check if external link
      if (isExternalLink(href)) {
        // Prevent default navigation
        event.preventDefault();
        // Stop propagation to prevent Phoenix component handlers
        event.stopPropagation();

        // Copy link to clipboard
        copyToClipboard(href)
          .then(() => {
            globalAlertStore.showAlert({
              translation: {
                title: '',
                subTitle: '链接已复制，请在浏览器打开',
                primaryButtonTitle: '确定',
              },
              primaryHandleClick: () => {
                globalAlertStore.hideAlert();
              },
            });
          })
          .catch(() => {
            globalAlertStore.showAlert({
              translation: {
                title: '',
                subTitle: `复制失败，请手动复制链接：${href}`,
                primaryButtonTitle: '确定',
              },
              primaryHandleClick: () => {
                globalAlertStore.hideAlert();
              },
            });
          });
      }
    };

    // Use capture phase to intercept before Phoenix component handlers
    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);
};
